<template>
  <section
    class="chart-panel border rounded-3 px-3 pt-3 pb-3"
    :aria-label="t('charts.panel.region_label')"
  >
    <!-- Always visible: what the table's filters leave, and the collapse control -->
    <div class="chart-panel-header d-flex align-items-center flex-wrap gap-3">
      <button
        type="button"
        class="btn btn-link p-0 d-flex align-items-center gap-2 fw-bold text-body-emphasis text-decoration-none"
        :aria-expanded="collapsed ? 'false' : 'true'"
        :aria-controls="bodyId"
        @click="collapsed = !collapsed"
      >
        <FontAwesomeIcon
          icon="chevron-right"
          class="chart-chevron"
          :class="{ 'chart-chevron--open': !collapsed }"
        />
        {{ t("charts.panel.title") }}
      </button>
      <span class="vr"></span>
      <span aria-live="polite" class="small text-body-tertiary">
        <strong class="summary-total text-body-emphasis">
          {{ t("charts.panel.mwh", { mwh: formatNumberWithSpaces(totalMwh) }) }}
        </strong>
        {{
          t(
            "charts.panel.summary_rows",
            { available: formatNumberWithSpaces(availableMwh) },
            rowCount
          )
        }}
      </span>
      <div v-if="collapsed" class="ms-auto d-flex align-items-center gap-2">
        <label :for="breakdownSelectId" class="small text-body-tertiary mb-0">
          {{ t("charts.panel.break_down_by") }}
        </label>
        <select
          :id="breakdownSelectId"
          v-model="breakdownIndex"
          class="form-select form-select-sm w-auto"
        >
          <option
            v-for="(option, index) in breakdownOptions"
            :key="option.fieldName"
            :value="index"
          >
            {{ t(option.labelKey, option.label) }}
          </option>
        </select>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
        :class="{ 'ms-auto': !collapsed }"
        @click="collapsed = !collapsed"
      >
        <FontAwesomeIcon icon="chart-column" />
        {{ collapsed ? t("charts.panel.show") : t("charts.panel.hide") }}
      </button>
    </div>

    <div :id="bodyId">
      <ChartBreakdownBar
        v-if="collapsed"
        class="mt-3"
        :items="rawData"
        :stackOption="selectedBreakdown"
        :y-field="yAxisFieldName"
        @pick="$emit('filter-to', selectedBreakdown.fieldName, $event)"
      />

      <div v-else class="mt-3">
        <!-- Dropdowns for selecting X-Axis and Stack options -->
        <div class="row mb-2">
          <div class="col-md-6">
            <label :for="xAxisSelectId" class="form-label fs-9"
              >{{ t("charts.xAxisLabel") }}:</label
            >
            <select
              :id="xAxisSelectId"
              class="form-select form-select-sm"
              v-model="selectedXAxisIndex"
            >
              <option
                v-for="(option, index) in xAxisOptions"
                :key="`x-${option.fieldName}`"
                :value="index"
              >
                {{ t(option.labelKey, option.label) }}
              </option>
            </select>
          </div>
          <div class="col-md-6">
            <label :for="stackSelectId" class="form-label fs-9"
              >{{ t("charts.stackByLabel") }}:</label
            >
            <select
              :id="stackSelectId"
              class="form-select form-select-sm"
              v-model="selectedStackIndex"
            >
              <option
                v-for="(option, index) in stackOptions"
                :key="`s-${option.fieldName}`"
                :value="index"
              >
                {{ t(option.labelKey, option.label) }}
              </option>
            </select>
          </div>
        </div>

        <StackedBarChart
          v-if="shouldRenderChart"
          :chart-data="chartData"
          :y-axis-title="yAxisTitle"
          :height="chartHeight"
        />
        <PlaceholderCardComponent v-else :title="t('charts.noDataMessage')" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import StackedBarChart from "@/common/StackedBarChart.vue";
import PlaceholderCardComponent from "@/components/common/PlaceholderCardComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { useQueryManager } from "@/composables/useQueryManager";
import { useStackableDataTransformer } from "@/composables/useStackableDataTransformer";
import ChartBreakdownBar from "@/dashboard/inventory_management/overview/ChartBreakdownBar.vue";
import type {
  BreakdownColumn,
  BreakdownOption,
} from "@/dashboard/inventory_management/overview/breakdownSegments";
import {
  RawDataItem,
  StackableDataset,
  StackableTransform,
  StackOption,
  XAxisOption,
} from "@/types/ChartData";
import { useLocalStorage } from "@vueuse/core";
import { computed, onMounted, ref, toRef, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Visualises rows the table has already filtered; it has no filters of its own. Collapsed, it
 * shows a one-line breakdown whose segments ask the parent (via `filter-to`) to filter the table.
 */
const props = defineProps<{
  panelPrefix: string; // Unique prefix for URL query params and the collapsed preference
  rawData: Readonly<RawDataItem[]>;
  xAxisOptions: XAxisOption[];
  stackOptions: StackOption[];
  /** Fields the collapsed breakdown bar can split by; never empty. */
  breakdownOptions: BreakdownOption[];
  yAxisFieldName: string;
  chartHeight: number;
  totalMwh: number;
  rowCount: number;
  /** MWh of the rows that can be selected. */
  availableMwh: number;
}>();

defineEmits<{
  "filter-to": [column: BreakdownColumn, value: string | null];
}>();

const { t } = useI18n();
const { getQueryValue, parsers } = useQueryManager();

const bodyId = useId();
const breakdownSelectId = useId();
const xAxisSelectId = useId();
const stackSelectId = useId();

// --- Internal Component State Refs ---
const collapsed = useLocalStorage(
  `${props.panelPrefix}-chart-collapsed`,
  false
);
const breakdownIndex = ref(0);
const selectedXAxisIndex = ref(0);
const selectedStackIndex = ref(0);

// --- Query Parameter State Refs (using getQueryValue) ---
const xAxisQuery = getQueryValue(`${props.panelPrefix}_x`, parsers.number);
const stackQuery = getQueryValue(`${props.panelPrefix}_s`, parsers.number);

const selectedBreakdown = computed(
  () => props.breakdownOptions[breakdownIndex.value]
);

// --- Initialization: Read from Query Refs -> Update Local State ---
onMounted(() => {
  const initialX = xAxisQuery.value;
  const initialS = stackQuery.value;

  if (initialX !== undefined) {
    selectedXAxisIndex.value = Math.max(
      0,
      Math.min(initialX, props.xAxisOptions.length - 1)
    );
  }
  if (initialS !== undefined) {
    selectedStackIndex.value = Math.max(
      0,
      Math.min(initialS, props.stackOptions.length - 1)
    );
  }
});

// --- Synchronization: Watch Local State -> Update Query Refs ---
watch(selectedXAxisIndex, (newValue) => {
  xAxisQuery.value = newValue;
});

watch(selectedStackIndex, (newValue) => {
  stackQuery.value = newValue;
});

const transform = computed<StackableTransform>(() => {
  const selectedXAxis = props.xAxisOptions[selectedXAxisIndex.value];
  const selectedStack = props.stackOptions[selectedStackIndex.value];

  // A malformed URL index (`<prefix>_x=abc` parses to NaN, which the clamp keeps) selects no
  // option; plot nothing rather than crash the transformer.
  if (!selectedXAxis || !selectedStack) {
    console.error("Selected X-Axis or Stack option is invalid.");
    return {
      xAxisOption: { fieldName: "", label: "", labelKey: "" },
      stackOption: {
        fieldName: "",
        label: "",
        labelKey: "",
        styleMap: {},
      },
      yAxis: props.yAxisFieldName,
    };
  }

  return {
    xAxisOption: selectedXAxis,
    stackOption: selectedStack,
    yAxis: props.yAxisFieldName,
  };
});

const { stackableDataset, uniqueXValues } = useStackableDataTransformer(
  toRef(props, "rawData"),
  transform
);

const chartData = computed(() => ({
  labels: uniqueXValues.value,
  datasets: stackableDataset.value as StackableDataset[],
}));

const yAxisTitle = "MWh";

// Render the chart only when the transformer has something to plot. Deciding from raw
// row presence would mount a blank chart when the filters exclude every item or no item
// has a value on the selected x-axis; the placeholder then asks the user to adjust filters.
const shouldRenderChart = computed(() => uniqueXValues.value.length > 0);
</script>

<style scoped lang="scss">
.chart-panel-header {
  min-height: 36px;
}

.summary-total {
  font-size: 1.125rem;
}

.chart-chevron {
  font-size: 0.75rem;
  transition: transform 0.15s ease;

  &--open {
    transform: rotate(90deg);
  }
}
</style>
